import {AuthModel} from './_models'
import { refreshToken } from './_requests'
import axios from 'axios'

const AUTH_LOCAL_STORAGE_KEY = 'impersonating-auth-token'
const getAuth = (): AuthModel | undefined => {
  if (!localStorage) {
    return
  }

  const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY)
  if (!lsValue) {
    return
  }

  try {
    const auth: AuthModel = JSON.parse(lsValue) as AuthModel
    if (auth) {
      // You can easily check auth_token expiration also
      return auth
    }
  } catch (error) {
    console.error('AUTH LOCAL STORAGE PARSE ERROR', error)
  }
}

/**
 * Se a falha do refresh do token encerra a sessão. Segue o contrato de POST /api/auth/refresh
 * (taskrush-backend docs/features/session.md): só o 401 (sessão expirada, token inválido ou revogado).
 * O 400 era "token ainda válido" (outra requisição ou aba já tinha renovado) e deslogava quem estava
 * logado. Qualquer outro status (429 do limite da API, 5xx, falha de rede sem status) mantém a
 * sessão: a requisição falha e a próxima tenta renovar de novo.
 */
const refreshEndsSession = (status?: number): boolean => status === 401

/**
 * Se a requisição que tomou 401 saiu com um token diferente do salvo agora, outra requisição ou aba
 * já renovou: basta repetir com o token atual, sem chamar o refresh de novo.
 */
const tokenChangedSince = (sentAuthorization: unknown, currentToken?: string): boolean =>
  !!currentToken &&
  typeof sentAuthorization === 'string' &&
  sentAuthorization !== `Bearer ${currentToken}`

/**
 * Fim da sessão: apaga o token e vai para o login na hora, recarregando a página. Sem isso as telas
 * abertas continuavam chamando a API sem token até o usuário recarregar, e esse volume esgotava o
 * limite da API por IP.
 */
const endSession = () => {
  removeAuth()
  if (!window.location.pathname.startsWith('/auth')) {
    window.location.replace('/auth')
  }
}

const setAuth = (auth: AuthModel) => {
  if (!localStorage) {
    return
  }

  try {
    const lsValue = JSON.stringify(auth)
    localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, lsValue)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE SAVE ERROR', error)
  }
}

const removeAuth = () => {
  if (!localStorage) {
    return
  }

  try {
    localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY)
  } catch (error) {
    console.error('AUTH LOCAL STORAGE REMOVE ERROR', error)
  }
}

export function setupAxios(axios: any) {
  axios.defaults.headers.Accept = 'application/json'

  let isRefreshing = false
  type Queued401 = {resolve: (value: unknown) => void; reject: (reason?: unknown) => void; config: any}
  let refreshSubscribers: Queued401[] = []

  function flushQueueSuccess(token: string) {
    const pending = refreshSubscribers
    refreshSubscribers = []
    pending.forEach(({resolve, config}) => {
      if (config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      resolve(axios(config))
    })
  }

  function flushQueueError(err: unknown) {
    const pending = refreshSubscribers
    refreshSubscribers = []
    pending.forEach(({reject}) => reject(err))
  }

  axios.interceptors.request.use(
    (config: {headers: {Authorization: string}; url?: string}) => {
      // Não adicionar token do sistema principal em requisições do portal do cliente
      if (config.url?.includes('/api/client-portal/')) {
        return config
      }
      
      const auth = getAuth()
      if (auth && auth.api_token) {
        config.headers.Authorization = `Bearer ${auth.api_token}`
      }

      return config
    },
    (err: any) => Promise.reject(err)
  )

  axios.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
      // Não processar erros 401 de requisições do portal do cliente
      if (error.config?.url?.includes('/api/client-portal/')) {
        return Promise.reject(error);
      }
      
      if (error.response?.status === 401) {
        const originalRequest = error.config
        const reqUrl = String(originalRequest?.url ?? '')

        // Evita loop: refresh/logout que retornam 401 não devem re-disparar refresh. O 401 do login
        // é senha errada, não sessão vencida.
        if (
          reqUrl.includes('/api/auth/refresh') ||
          reqUrl.includes('/api/auth/logout') ||
          reqUrl.includes('/api/auth/login')
        ) {
          return Promise.reject(error)
        }

        if (originalRequest._retry) {
          return Promise.reject(error)
        }
        originalRequest._retry = true

        const currentToken = getAuth()?.api_token
        if (tokenChangedSince(originalRequest.headers?.Authorization, currentToken)) {
          originalRequest.headers.Authorization = `Bearer ${currentToken}`
          return axios(originalRequest)
        }

        if (!isRefreshing) {
          isRefreshing = true
          try {
            const refreshResponse = await refreshToken()
            const newToken = refreshResponse.data.api_token

            const auth = getAuth()
            if (auth) {
              auth.api_token = newToken
              setAuth(auth)
            }

            axios.defaults.headers.Authorization = `Bearer ${newToken}`
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`
            }

            flushQueueSuccess(newToken)
            return axios(originalRequest)
          } catch (refreshError: any) {
            console.error('Erro ao atualizar token', refreshError)
            if (refreshEndsSession(refreshError?.response?.status)) {
              endSession()
            }
            flushQueueError(refreshError)
            return Promise.reject(refreshError)
          } finally {
            isRefreshing = false
          }
        }

        return new Promise((resolve, reject) => {
          refreshSubscribers.push({resolve, reject, config: originalRequest})
        })
      }
  
      return Promise.reject(error);
    }
  );
}

const callLogoutAPI = async () => {
  const API_URL = process.env.REACT_APP_API_URL
  const auth = getAuth()
  
  if (auth?.api_token) {
    try {
      await axios.post(`${API_URL}/api/auth/logout`)
    } catch (error) {
      console.error('Erro ao chamar API de logout:', error)
      throw error
    }
  }
}

export {getAuth, setAuth, removeAuth, callLogoutAPI, refreshEndsSession, tokenChangedSince, AUTH_LOCAL_STORAGE_KEY}
